import { faCheck, faCopy, faShare } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  SPACING,
  FamilyChildProfile,
  JoonUIColor,
  Modal,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { QUERY_KEYS } from "../../constants"
import useMarkMissionComplete from "../../hooks/useMarkMissionComplete"
import useMobile from "../../hooks/useMobile"
import i18n from "../../locales/i18n"
import { useChildAuthCodeQuery, useFamilyQuery } from "../../networking/queries"
import { FamilyQuerySelect, MissionScene } from "../../types"
import { queryClient } from "../../util/queryClient"

interface GetChildStartedModalStore {
  isOpen: boolean
  onOpen: (childId?: number) => void
  onClose: () => void
  childId: number | null // if child is not passed, don't show button
  error: string | null
  setError: (error: string | null) => void
}

export const useGetChildStartedModal = create<GetChildStartedModalStore>(
  (set) => ({
    isOpen: false,
    onOpen: (childId) => set({ isOpen: true, childId }),
    onClose: () => set({ isOpen: false, childId: null, error: null }),
    childId: null,
    error: null,
    setError: (error) => set({ error }),
  })
)

const downloadLink = "https://joonapp.io/download-redirect"
const openDownloadLink = () => window.open(downloadLink, "_blank")

const GetChildStartedModal = () => {
  const { isOpen, onClose, childId } = useGetChildStartedModal()

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      displayCloseIcon
      animate
      style={{ width: "min(400px, 95vw)" }}
    >
      <GetChildStartedContent childId={childId || null} />
    </Modal>
  )
}

export const GetChildStartedContent = ({
  childId, // needs userId to check if linked
}: {
  childId: number | null
}) => {
  const { onClose, error, setError } = useGetChildStartedModal()
  const { t } = useTranslation()
  const { refetch, isFetching } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const isMobile = useMobile()
  const { data: authCode } = useChildAuthCodeQuery()
  const [isCopied, setIsCopied] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const markMissionComplete = useMarkMissionComplete(MissionScene.FAMILY_TAB)

  const checkIfChildHasChosenDoter = () => {
    refetch().then((query) => {
      if (childId === null) return onClose()
      const child = query?.data?.find(
        (child: FamilyChildProfile) => child.user.id === childId
      )
      if (!!child?.user.active_doter.type) {
        queryClient.invalidateQueries([
          QUERY_KEYS.QUEST_INSTANCE_STATS,
          child.user.id,
        ])
        markMissionComplete()
        onClose()
      } else setError(t("GET_CHILD_STARTED_ERROR_NO_PET"))
    })
  }

  const clickShare = () => {
    if (!authCode) return
    if (isMobile) shareDownloadLink(authCode)
    else openDownloadLink()
  }

  const copyAuthCode = () => {
    if (!authCode) return
    navigator.clipboard.writeText(authCode)
    setIsCopied(true)
    setShowTooltip(true)
    setTimeout(() => setShowTooltip(false), 1000)
  }

  return (
    <div className="flex flex-col items-center p-6 gap-4 w-full bg-backgroundPrimary rounded-md">
      <Typography variant="body" textAlign="center" className="max-w-[275px]">
        {t("GET_CHILD_STARTED_DOWNLOAD")}
      </Typography>
      <Button
        onClick={() =>
          window.open("https://joonapp.io/download-redirect", "_blank")
        }
        buttonType="secondary"
        size="small"
        className="shadow-md"
        style={{
          borderColor: JoonUIColor.border.default,
          padding: SPACING.space3,
        }}
        text={
          <div className="flex gap-2">
            <img
              src={"/images/logos/joon-pet-game-logo.svg"}
              alt=""
              className="w-12 h-12"
            />
            <div className="flex flex-col text-left">
              <Typography variant="h3">Joon Pet Game</Typography>
              <Typography
                variant="bodyBold"
                color={JoonUIColor.text.primaryAccent}
              >
                {t("GET_CHILD_STARTED_VIEW_APP_STORE")}
              </Typography>
            </div>
          </div>
        }
      >
        <div className="flex p-3 border border-borderPrimary rounded-lg gap-2 shadow-md">
          <img
            src={"/images/logos/joon-pet-game-logo.svg"}
            alt=""
            className="w-12 h-12"
          />
          <div className="flex flex-col text-left">
            <Typography variant="h3">Joon Pet Game</Typography>
            <Typography
              variant="bodyBold"
              color={JoonUIColor.text.primaryAccent}
            >
              View in App Store
            </Typography>
          </div>
        </div>
      </Button>
      <Typography variant="bodySmall" textAlign="center">
        {t("GET_CHILD_STARTED_OR")}
      </Typography>

      <Button
        onClick={clickShare}
        buttonType="secondary"
        size="small"
        text={t("GET_CHILD_STARTED_SHARE")}
        icon={faShare}
        className="shadow-sm"
        style={{ borderColor: JoonUIColor.border.default }}
      />

      <Typography variant="bodySmall" textAlign="center">
        {t("GET_CHILD_STARTED_LOGIN_CODE")}
      </Typography>

      <div className="relative">
        {showTooltip && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 px-3 py-1 rounded">
            <Typography variant="bodySmall" color={JoonUIColor.text.inverted}>
              {t("GET_CHILD_STARTED_COPIED")}
            </Typography>
          </div>
        )}
        <button onClick={copyAuthCode}>
          <div className="flex flex-row items-center gap-2 border-borderPrimary rounded-md shadow-md p-4 border">
            <Typography
              variant="h3"
              color={JoonUIColor.text.primaryAccent}
              style={{ userSelect: "text" }}
            >
              {authCode}
            </Typography>
            <FontAwesomeIcon
              icon={isCopied ? faCheck : faCopy}
              fontSize={16}
              color={
                isCopied
                  ? JoonUIColor.semantic.success
                  : JoonUIColor.text.primaryAccent
              }
            />
          </div>
        </button>
      </div>

      <Typography
        variant="bodySmall"
        textAlign="center"
        className="max-w-[290px]"
      >
        {t("GET_CHILD_STARTED_FOLLOW")}
      </Typography>
      {error && (
        <Typography variant="bodySmallBold" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      <Button
        onClick={checkIfChildHasChosenDoter}
        isLoading={isFetching}
        text={t("GET_CHILD_STARTED_DONE")}
        fullWidth
      />
    </div>
  )
}

const shareDownloadLink = async (code: string) => {
  const subjectNew = i18n.t("GET_CHILD_STARTED_SHARE_SUBJECT")
  const instructionsNew = i18n.t("GET_CHILD_STARTED_SHARE_BODY", {
    code,
    link: downloadLink,
  })

  try {
    // If the web share API is supported by the browser
    if (navigator.share) {
      await navigator.share({
        title: subjectNew,
        text: instructionsNew,
        url: "https://joonapp.io/download-redirect",
      })
    } else {
      // Else open email to share
      const emailSubject = encodeURIComponent(subjectNew)
      const emailBody = encodeURIComponent(instructionsNew)

      const url = `mailto:?subject=${emailSubject}&body=${emailBody}`
      window.open(url)
    }
  } catch (error) {
    console.error("Error sharing:", error)
  }
}

export default GetChildStartedModal
